import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FiX, FiGrid, FiChevronDown, FiChevronRight, FiSearch } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { categories, metrics } from '../data/metrics';
import clsx from 'clsx';

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const navigate = useNavigate();
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef(null);
  const resultsRef = useRef([]);

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  const handleMetricClick = (metricId) => {
    navigate(`/calculator/${metricId}`);
    setSearchQuery('');
    setSearchResults([]);
    if (window.innerWidth < 1024) {
      toggleSidebar();
    }
  };

  // Search functionality
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchResults([]);
      setSelectedIndex(0);
      return;
    }

    const query = searchQuery.toLowerCase();
    const results = metrics.filter(metric => {
      const nameMatch = metric.name.toLowerCase().includes(query);
      const fullNameMatch = metric.fullName?.toLowerCase().includes(query);
      const definitionMatch = metric.definition.toLowerCase().includes(query);
      const categoryName = categories.find(c => c.id === metric.categoryId)?.name.toLowerCase();
      const categoryMatch = categoryName?.includes(query);
      
      return nameMatch || fullNameMatch || definitionMatch || categoryMatch;
    });

    // Sort results by relevance
    results.sort((a, b) => {
      const aNameMatch = a.name.toLowerCase().startsWith(query);
      const bNameMatch = b.name.toLowerCase().startsWith(query);
      
      if (aNameMatch && !bNameMatch) return -1;
      if (!aNameMatch && bNameMatch) return 1;
      return 0;
    });

    setSearchResults(results);
    setSelectedIndex(0);
  }, [searchQuery]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (searchResults[selectedIndex]) {
          handleMetricClick(searchResults[selectedIndex].id);
        }
        break;
      case 'Escape':
        setSearchQuery('');
        setSearchResults([]);
        searchInputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current[selectedIndex]) {
      resultsRef.current[selectedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [selectedIndex]);

  // Highlight matching text
  const highlightMatch = (text, query) => {
    if (!query.trim()) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={index} className="bg-yellow-200 text-slate-900 font-bold rounded px-0.5">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    searchInputRef.current?.focus();
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden backdrop-blur-sm"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar Content */}
      <aside
        className={clsx(
          "fixed inset-y-0 left-0 z-50 w-80 bg-jeruel-blue text-white transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 shadow-xl",
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo Area - Centered */}
          <div className="h-20 flex items-center justify-center relative border-b border-jeruel-light/30">
            <div className="w-full flex justify-center items-center px-6">
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1772024462043-Jeruel%20Logo%20White.png" 
                alt="Jeruel Global" 
                className="h-10 w-auto object-contain"
              />
            </div>
            <button
              onClick={toggleSidebar}
              className="lg:hidden text-white/60 hover:text-white transition-colors absolute right-6"
            >
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="p-4 border-b border-white/10">
            <div className="relative">
              <SafeIcon 
                icon={FiSearch} 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-lg pointer-events-none" 
              />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search metrics..."
                className="w-full bg-white/10 border border-white/20 rounded-lg pl-10 pr-10 py-2.5 text-white placeholder-white/40 focus:bg-white/15 focus:border-white/40 focus:outline-none transition-all text-sm"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                >
                  <SafeIcon icon={FiX} className="text-lg" />
                </button>
              )}
            </div>

            {/* Search Results Dropdown */}
            {searchResults.length > 0 && (
              <div className="absolute left-4 right-4 mt-2 bg-white rounded-lg shadow-2xl border border-jeruel-grayblue max-h-96 overflow-y-auto z-50 custom-scrollbar">
                <div className="p-2 border-b border-slate-100 bg-slate-50">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                    {searchResults.length} {searchResults.length === 1 ? 'Result' : 'Results'} Found
                  </p>
                </div>
                <div className="py-1">
                  {searchResults.map((metric, index) => {
                    const category = categories.find(c => c.id === metric.categoryId);
                    return (
                      <button
                        key={metric.id}
                        ref={el => resultsRef.current[index] = el}
                        onClick={() => handleMetricClick(metric.id)}
                        onMouseEnter={() => setSelectedIndex(index)}
                        className={clsx(
                          "w-full text-left px-3 py-2.5 transition-colors group",
                          selectedIndex === index
                            ? "bg-jeruel-soft"
                            : "hover:bg-slate-50"
                        )}
                      >
                        <div className="flex items-start gap-2 mb-1">
                          <span className={clsx(
                            "px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide mt-0.5 flex-shrink-0",
                            category?.bg,
                            category?.color
                          )}>
                            {category?.name.split(' ')[0]}
                          </span>
                          <h4 className="font-bold text-sm text-slate-900 leading-tight">
                            {highlightMatch(metric.name, searchQuery)}
                          </h4>
                        </div>
                        <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed pl-0">
                          {highlightMatch(
                            metric.definition.substring(0, 120) + 
                            (metric.definition.length > 120 ? '...' : ''),
                            searchQuery
                          )}
                        </p>
                      </button>
                    );
                  })}
                </div>
                <div className="p-2 border-t border-slate-100 bg-slate-50">
                  <p className="text-[10px] text-slate-400 text-center">
                    Use ↑↓ to navigate • Enter to select • Esc to close
                  </p>
                </div>
              </div>
            )}

            {/* No Results */}
            {searchQuery && searchResults.length === 0 && (
              <div className="absolute left-4 right-4 mt-2 bg-white rounded-lg shadow-2xl border border-jeruel-grayblue z-50 p-6 text-center">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <SafeIcon icon={FiSearch} className="text-slate-400 text-xl" />
                </div>
                <p className="text-sm font-bold text-slate-700 mb-1">No metrics found</p>
                <p className="text-xs text-slate-500">
                  Try searching with different keywords
                </p>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-6 px-3 space-y-1 custom-scrollbar">
            <NavLink
              to="/"
              className={({ isActive }) =>
                clsx(
                  "flex items-center px-4 py-3 rounded-lg transition-all duration-200 group",
                  isActive
                    ? 'bg-white text-jeruel-blue shadow-lg'
                    : 'text-white/70 hover:bg-jeruel-light hover:text-white'
                )
              }
            >
              <SafeIcon icon={FiGrid} className="mr-3 text-lg" />
              <span className="font-medium">Dashboard</span>
            </NavLink>

            <div className="pt-6 pb-2 px-4 text-xs font-bold text-white/40 uppercase tracking-wider">
              Metrics by Category
            </div>

            {categories.map((cat) => {
              const catMetrics = metrics.filter((m) => m.categoryId === cat.id);
              const isExpanded = expandedCategory === cat.id;

              return (
                <div key={cat.id} className="space-y-1">
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(cat.id)}
                    className={clsx(
                      "w-full flex items-center justify-between px-4 py-2.5 rounded-lg transition-all duration-200 text-sm group",
                      isExpanded
                        ? 'bg-jeruel-light text-white'
                        : 'text-white/70 hover:bg-jeruel-light hover:text-white'
                    )}
                  >
                    <div className="flex items-center">
                      <SafeIcon
                        name={cat.icon}
                        className={clsx(
                          "mr-3 text-lg transition-colors",
                          isExpanded ? "text-white" : "text-white/50 group-hover:text-white/80"
                        )}
                      />
                      <span className="flex-1 text-left">{cat.name}</span>
                    </div>
                    <SafeIcon
                      icon={isExpanded ? FiChevronDown : FiChevronRight}
                      className="w-4 h-4 transition-transform"
                    />
                  </button>

                  {/* Metrics List */}
                  {isExpanded && (
                    <div className="ml-4 pl-4 border-l-2 border-white/10 space-y-0.5 py-1">
                      {catMetrics.map((metric) => (
                        <button
                          key={metric.id}
                          onClick={() => handleMetricClick(metric.id)}
                          className="w-full text-left px-3 py-2 rounded-md text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors"
                        >
                          {metric.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Footer Card */}
          <div className="p-4 border-t border-white/20">
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <h4 className="text-sm font-medium text-white mb-1">Reference Guide</h4>
              <p className="text-xs text-white/60 mb-3 leading-relaxed">
                Industry-standard maintenance and reliability metrics.
              </p>
              <div className="flex items-center text-xs text-white font-medium cursor-pointer hover:text-white/80 transition-colors">
                <span>View Documentation</span>
                <SafeIcon icon={FiChevronRight} className="ml-1 w-3 h-3" />
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;